import {
  DirectoryRecordData,
  DirectorySchemaData,
} from 'api/types/data/directory/directory';
import { CountryEntity } from 'api/types/entity';
import { convertDirectoryFieldValueToLabel } from 'helpers';
import { Langs, TFunc } from 'libs/i18n';
import { getDirectoryFieldValue } from 'modules/directory/common/helpers';

interface Props {
  t: TFunc;
  language: Langs;
  currentSchema?: DirectorySchemaData;
  currentRecord?: DirectoryRecordData;
  allSchemas?: DirectorySchemaData[];
  countries?: CountryEntity[];
}

export const getInitialValues = ({
  t,
  language,
  currentSchema,
  currentRecord,
  allSchemas,
  countries,
}: Props) => {
  if (!currentSchema || !currentRecord || !allSchemas) {
    return undefined;
  }

  currentRecord.fields = currentRecord.fields.map((field) => ({
    ...field,
    value: getDirectoryFieldValue(field, currentSchema, allSchemas),
  }));

  return {
    ...currentRecord.fields.reduce((acc, field) => {
      if (!field.name) {
        return acc;
      }
      const fieldSchema = currentSchema.fieldDefinitions.find(
        (fieldDefinition) => fieldDefinition.name === field.name
      );
      const convertedValueLabel = convertDirectoryFieldValueToLabel({
        t,
        language,
        fieldValue: field.value,
      });

      switch (fieldSchema?.component) {
        case 'Select':
        case 'Multiselect':
          if (field.referenceRecord) {
            acc[field.name] = {
              label: convertedValueLabel,
              value: field.referenceRecord.id,
              field,
            };
          } else if (convertedValueLabel) {
            acc[field.name] = {
              label: convertedValueLabel,
              value: field.value,
            };
          }
          break;
        case 'Datetime':
          const date = new Date(field.value as string);
          acc[field.name] = date;
          break;
        case 'Country':
          acc[field.name] = field.value
            ? {
                label: countries?.find((country) => country.id === field.value)
                  ?.name,
                value: field.value,
              }
            : undefined;
          break;

        case 'Decimal':
          acc[field.name] = String(convertedValueLabel);
          break;
        case 'Input':
          if (fieldSchema.isMultiLanguage) {
            acc[field.name] = {
              ru: String(
                convertDirectoryFieldValueToLabel({
                  t,
                  language: 'ru',
                  fieldValue: field.value,
                })
              ),
              en: String(
                convertDirectoryFieldValueToLabel({
                  t,
                  language: 'en',
                  fieldValue: field.value,
                })
              ),
            };
          } else {
            acc[field.name] = String(convertedValueLabel);
          }
          break;
        default:
          acc[field.name] = convertedValueLabel;
          break;
      }

      return acc;
    }, {} as Record<string, any>),
  };
};
